import { Database } from "@/lib/supabase";
import { JobItemTypeEnum } from "@/lib/supabase/supabaseEnums";
import {
	ClientEntityType,
	GuarantorEntityType,
	JobDocumentEntityType,
	JobEntityType,
	JobItemEntityType,
	OrganizationEntityType,
} from "@/lib/supabase/supabaseTypes";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { JobDocumentTypeEnum } from "../../pages/job-page/job-document/job-document.types";
import { Logger } from "@/lib/logger/Logger";
import { findEarliestAndLatest } from "./utils-functions";
import { RoundToType } from "../../store/Jobs/utils";

const emptyJobTotals = {
	taxpunkte: getCalculationResult(0),
	amountPostage: getCalculationResult(0),
	amountExternalWork: getCalculationResult(0),
	amountDiscounts: getCalculationResult(0),
	amountWorkAndFixedRate: getCalculationResult(0),
	deductions: getCalculationResult(0),
	deductionsWorkAndFixedRate: getCalculationResult(0),
	deductionsMaterials: getCalculationResult(0),
	amountMaterials: getCalculationResult(0),
	subTotal: getCalculationResult(0),
	mwst: getCalculationResult(0),
	total: getCalculationResult(0),
	exemptFromTaxAmountExclVat: getCalculationResult(0),
	exemptFromTaxAmountInclVat: getCalculationResult(0),
	subjectToDrTaxAmountExclVat: getCalculationResult(0),
	subjectToDrTaxAmountInclVat: getCalculationResult(0),
	mwstPercentage: "",
	roundingDifference: getCalculationResult(0),
};

interface CalculationResult {
	text: string;
	value: number;
}

export type JobItemsForCalculations = {
	id: string;
	price: number;
	quantity: number;
	tp_value: number;
	discount: number;
	is_req_dr_tax: boolean;
	is_warranty: boolean;
	type: JobItemTypeEnum;
};

export type JobItemsFromExtendedView = {
	id: string;
	price: number;
	quantity: number;
	tp_value: number;
	discount: number;
	is_req_dr_tax: boolean;
	is_warranty: boolean;
	type: JobItemTypeEnum;
	code: string;
	code_e: string;
	description: string;
	note: string;
};

export type ExtendedJobDocument = Omit<
	Database["public"]["Views"]["job_documents_extended_view"]["Row"],
	"job_items"
> & {
	job_items: JobItemsFromExtendedView[];
};

interface SammelrechnungRow {
	jobCode: string;
	clientName: string;
	jobDocumentDate: string;
	jobTitle: string;
	jobTotals: CalculateTotalsResult;
	patientName: {
		firstName: string;
		lastName: string;
		title: string;
		birth_date: string;
		code: string;
	};
	practitionerName: {
		firstName: string;
		lastName: string;
		title: string;
		id: number | null;
	};
	ignoreMonthlyDiscount: boolean;
}

interface VatLine {
	rate: number;
	net: number;
	vat: number;
}

interface CalculateSammelrechnungResult extends CalculateTotalsResult {
	monthlyDiscountOnWorkAndFixedRate: CalculationResult;
	monthlyDiscountOnSubtotal: CalculationResult;
	startDate: string | null;
	endDate: string | null;
	rows: SammelrechnungRow[];
	vatLines: VatLine[];
	jobMwstNumber?: string;
}

function getCalculationResult(value: number): CalculationResult {
	return {
		text: value.toFixed(2),
		value,
	};
}

export function applyRounding(
	value: number,
	roundingType: RoundToType = RoundToType.TO_RAPPEN
): number {
	switch (roundingType) {
		case RoundToType.TO_INTEGER:
			return Math.round(value);
		case RoundToType.TO_FIRST_DECIMAL:
			return Math.round(value * 10) / 10;
		case RoundToType.TO_SECOND_DECIMAL:
			return Math.round(value * 100) / 100;
		case RoundToType.TO_THIRD_DECIMAL:
			return Math.round(value * 1000) / 1000;
		case RoundToType.TO_RAPPEN:
		default:
			return Math.round(value * 20) / 20; // Round to nearest 0.05
	}
}

export const roundToCents = (value: number): number =>
	applyRounding(value, RoundToType.TO_SECOND_DECIMAL);

function getMwstPercentage(jobTaxRate: number | null): number {
	if (jobTaxRate !== null && jobTaxRate !== undefined && jobTaxRate >= 0) {
		return jobTaxRate;
	}
	return 0;
}

export const getMwStPercentageForSammelrechnung = (
	extendedJobDocuments: ExtendedJobDocument[]
): {
	mwstPercentage: number;
	error: boolean;
} => {
	const firstTaxRate = extendedJobDocuments[0]?.job_tax_rate;
	const hasMissingTaxRate =
		firstTaxRate === null || firstTaxRate === undefined;
	const hasMixedTaxRates = extendedJobDocuments.some(
		(jobDocument) => jobDocument.job_tax_rate !== firstTaxRate
	);

	if (hasMixedTaxRates || hasMissingTaxRate) {
		return {
			mwstPercentage: 0,
			error: true,
		};
	}

	const mwstPercentage = firstTaxRate;
	return {
		mwstPercentage: mwstPercentage,
		error: false,
	};
};

/**
 * Calculates the final amount (Betrag) for a single job item
 *
 * Betrag = Menge * TP-Wert * Rabatt * Preis (amount = quantity * tp_value * discount * price)
 *
 * Note:
 * - Warranty is not included here because the full amount should still be shown in the table (it's only substracted from the total amount)
 * - Therefore, make sure to take out the warranty items before calculating the full amount
 *
 * @param {JobItemsForCalculations} jobItem
 * @returns {number} final amount (Betrag) for a single job item
 */
export function calculateJobItemAmount(
	jobItem: JobItemsForCalculations
): number {
	const amountWithoutDiscount =
		jobItem.price * jobItem.quantity * (jobItem.tp_value || 1);

	let discountAmount = 0;
	if (jobItem.discount && jobItem.discount > 0) {
		discountAmount = (jobItem.discount / 100) * amountWithoutDiscount;
	}
	return amountWithoutDiscount - discountAmount;
}

/** Adapter if you want to pass JobItemEntityType */
export function calculateJobItemAmountForJobItem(
	jobItem: JobItemEntityType
): number {
	return calculateJobItemAmount({
		id: jobItem.id,
		price: jobItem.price,
		quantity: jobItem.quantity,
		tp_value: jobItem.tp_value,
		discount: jobItem.discount,
		is_req_dr_tax: jobItem.is_req_dr_tax || false,
		is_warranty: jobItem.is_warranty || false,
		type: jobItem.type,
	});
}

export function calculateJobItemsSum(
	items: JobItemsForCalculations[],
	type?: JobItemTypeEnum
): number {
	return items.reduce((acc, curr) => {
		if (type === undefined || curr.type === type) {
			// Warranty means the item is free of charge, so we don't add anything to the sum
			if (curr.is_warranty) {
				return acc;
			}
			return acc + calculateJobItemAmount(curr);
		}
		return acc;
	}, 0);
}

/**
 * Calculate amount of job items that is subject to ZAZ Str.
 * If "Aerztesteuer inkl. Mwst" is selected, mwst is added on top of the amount
 */
export function calculateSubjectToDrTaxAmount(
	jobItems: JobItemsForCalculations[],
	mwstPercentage: number,
	showDrTaxWithMwst: boolean
) {
	const amountWithoutMwst = jobItems.reduce((acc, curr) => {
		// Warranty means the item is free of charge, so we don't add anything to the sum
		if (curr.is_warranty) {
			return acc;
		}
		if (
			curr.is_req_dr_tax &&
			curr.type !== JobItemTypeEnum.ARTICLE_DISCOUNTS
		) {
			return acc + calculateJobItemAmount(curr);
		}
		return acc;
	}, 0);
	if (!showDrTaxWithMwst) {
		return amountWithoutMwst;
	}
	const amountWithMwst = amountWithoutMwst * (1 + mwstPercentage / 100);
	return amountWithMwst;
}

/**
 * Calculate amount of job items that is subject to ZAZ Str.
 * Returns only VAT-exclusive amounts (base amounts without VAT markup)
 */
export function calculateSubjectToDrTaxAmounts(
	jobItems: JobItemsForCalculations[]
): number {
	const amountWithoutMwst = jobItems.reduce((acc, curr) => {
		// Warranty means the item is free of charge, so we don't add anything to the sum
		if (curr.is_warranty) {
			return acc;
		}
		if (
			curr.is_req_dr_tax &&
			curr.type !== JobItemTypeEnum.ARTICLE_DISCOUNTS
		) {
			return acc + calculateJobItemAmount(curr);
		}
		return acc;
	}, 0);

	// Always return VAT-exclusive amounts now
	return amountWithoutMwst;
}

export interface CalculateTotalsResult {
	amountMaterials: CalculationResult;
	amountPostage: CalculationResult;
	amountExternalWork: CalculationResult;
	amountDiscounts: CalculationResult;
	amountWorkAndFixedRate: CalculationResult;
	deductions: CalculationResult;
	deductionsWorkAndFixedRate: CalculationResult;
	deductionsMaterials: CalculationResult;
	subTotal: CalculationResult;
	mwst: CalculationResult;
	total: CalculationResult;
	taxpunkte: CalculationResult;
	exemptFromTaxAmountExclVat: CalculationResult;
	exemptFromTaxAmountInclVat: CalculationResult;
	subjectToDrTaxAmountExclVat: CalculationResult;
	subjectToDrTaxAmountInclVat: CalculationResult;
	mwstPercentage: string;
	akontoReallocation?: CalculationResult;
	roundingDifference?: CalculationResult;
}

interface CalculateJobTotalsInput {
	extendedJobDocument: ExtendedJobDocument;
	organization: OrganizationEntityType;
	client: ClientEntityType;
	akontoAmount?: number;
}

export function calculateJobTotalsLegacy({
	job,
	jobDocument,
	jobItems = [],
	organization,
	client,
	akontoAmount,
}: {
	job: JobEntityType | null;
	jobDocument: JobDocumentEntityType | null;
	jobItems: JobItemEntityType[];
	organization: OrganizationEntityType;
	client: ClientEntityType;
	akontoAmount?: number;
}): CalculateTotalsResult {
	return calculateJobTotals({
		extendedJobDocument: {
			job_code: job?.code || "",
			job_client_id: job?.client_id || "",
			job_document_date: jobDocument?.date || "",
			job_title: job?.title || "",
			job_document_discount_material: jobDocument?.discount_material || 0,
			job_document_discount_work: jobDocument?.discount_work || 0,
			job_document_id: Number(jobDocument?.id) || null,
			patient_code: "",
			patient_first_name: "",
			patient_last_name: "",
			patient_title: "",
			patient_birth_date: "",
			practitioner_id: null,
			practitioner_first_name: "",
			practitioner_last_name: "",
			practitioner_title: "",
			job_items: jobItems.map((item) => {
				return {
					id: item.id,
					price: item.price,
					quantity: item.quantity,
					tp_value: item.tp_value,
					discount: item.discount,
					is_req_dr_tax: item.is_req_dr_tax || false,
					is_warranty: item.is_warranty || false,
					type: item.type,
					code: "112",
					code_e: "112",
					description: "description",
					note: "",
				};
			}),
			organization_id: organization.id,
			job_document_type: jobDocument?.type || null,
			is_ignore_monthly_discount:
				job?.is_ignore_monthly_discount || false,
			job_tax_rate:
				job?.tax_rate !== null &&
				job?.tax_rate !== undefined &&
				job?.tax_rate >= 0
					? job?.tax_rate
					: null,
			job_document_amount: jobDocument?.amount || null,
			job_document_code: jobDocument?.code || null,
			job_document_created_at: jobDocument?.created_at || null,
			job_document_created_by: jobDocument?.created_by || null,
			job_document_deleted_at: jobDocument?.deleted_at || null,
			job_document_fae_id: jobDocument?.fae_id || null,
			job_document_job_id: jobDocument?.job_id || null,
			job_document_job_title: jobDocument?.work_title || null,
			job_document_modified_at: jobDocument?.modified_at || null,
			job_document_modified_by: jobDocument?.modified_by || null,
			job_document_note: jobDocument?.note || null,
			job_document_status: jobDocument?.status || null,
			job_document_title: jobDocument?.title || null,
			job_document_type_data: jobDocument?.type_data || null,
			job_document_work_title: jobDocument?.work_title || null,
			job_round_to:
				(job?.round_to as RoundToType) ?? RoundToType.TO_RAPPEN,
			job_currency_text: job?.currency_text || null,
		} as any,
		organization,
		client,
		akontoAmount,
	});
}

/**
 * Takes job items and calculates the total amount of work, materials, sub total, mwst and total
 * @param jobItems
 * @param job
 * @returns totals as strings rounded to 2 decimal places (except Taxpunkte)
 * @returns taxpunkte = Sum of (quantity * price) of all tariffs
 * @returns amountWork = Sum of (quantity * price * tp_value * discount) of all tariffs
 * @returns amountMaterials = Sum of (quantity * price * discount) of all materials
 * @returns amountPostage = Sum of (quantity * price * discount) of all postage
 * @returns amountExternalWork = Sum of (quantity * price * discount) of all external work
 * @returns amountDiscounts = Sum of (quantity * price * discount) of all discounts
 * @returns amountFixedRate = Sum of (quantity * price * discount) of all fixed rates
 * @returns deductions = amountDiscounts (fixed job item discount positions) + amountMaterials * discountMaterial + amountWork * discountWork
 * @returns subTotal = all job items (except discounts) - deductions
 * @returns mwst = mwst on subTotal
 * @returns total = subTotal + mwst
 * @returns exemptFromTaxAmount = total - subjectToTaxAmount
 * @returns subjectToTaxAmount = sum of all positions (except fixed discounts) that are marked is_req_dr_tax
 * @example   const {
    amountWork,
    amountMaterials,
    deductions,
    subTotal,
    mwst,
    total,
    taxpunkte,
  } = calculateJobTotals(jobItems, job);
 */
export function calculateJobTotals({
	extendedJobDocument,
	organization,
	client,
	akontoAmount,
}: CalculateJobTotalsInput): CalculateTotalsResult {
	const jobItems = extendedJobDocument.job_items;
	if (jobItems.length <= 0 || !organization) {
		return emptyJobTotals;
	}

	// Get rounding type from the extended job document
	const roundingType =
		(extendedJobDocument.job_round_to as RoundToType) ??
		RoundToType.TO_RAPPEN;

	// Calculate taxpunkte of all tariffs (Summe von (Menge * Preis) aller Zeilen)
	// Note that this calculation does not include discounts and does not include the TP-Wert (tp_value)
	const taxpunkte = jobItems.reduce((acc, curr) => {
		// Warranty means the item is free of charge, so we don't add anything to the sum
		if (curr.is_warranty) {
			return acc;
		}
		if (curr.type === JobItemTypeEnum.TARIFF) {
			return acc + curr.quantity * curr.price;
		}
		return acc;
	}, 0);

	// Caculate sum of all each item group
	// Note that job item level discounts are included in these calculations and are not part of later deductions

	// Work and fixed rate are work and fixed rate items that are not subject to discounts
	// Job document level discounts are applied to both
	const amountWork = calculateJobItemsSum(jobItems, JobItemTypeEnum.TARIFF);
	const amountFixedRate = calculateJobItemsSum(
		jobItems,
		JobItemTypeEnum.ARTICLE_FIXED_RATE
	);
	const amountWorkAndFixedRate = amountWork + amountFixedRate;

	const amountMaterials = calculateJobItemsSum(
		jobItems,
		JobItemTypeEnum.ARTICLE_MATERIAL
	);
	const amountPostage = calculateJobItemsSum(
		jobItems,
		JobItemTypeEnum.ARTICLE_POSTAGE
	);
	const amountExternalWork = calculateJobItemsSum(
		jobItems,
		JobItemTypeEnum.ARTICLE_EXTERNAL_WORK
	);

	// Discounts are a special item type that is used as a deduction
	const amountDiscounts = calculateJobItemsSum(
		jobItems,
		JobItemTypeEnum.ARTICLE_DISCOUNTS
	);

	// Job document level discounts are applied as a deduction
	const discountWork =
		(extendedJobDocument?.job_document_discount_work || 0) / 100;
	const discountMaterial =
		(extendedJobDocument?.job_document_discount_material || 0) / 100;

	const decuctionsWorkAndFixedRate = amountWorkAndFixedRate * discountWork;
	const decuctionsMaterials = amountMaterials * discountMaterial;

	const deductions =
		decuctionsWorkAndFixedRate + decuctionsMaterials + amountDiscounts;

	// Sum of all items
	const subTotal = roundToCents(
		amountWorkAndFixedRate +
			amountMaterials +
			amountPostage +
			amountExternalWork -
			deductions
	);

	// Note: Die MwSt. wird auf den Nettobetrag (Subtotal) berechnet
	const mwstPercentage = getMwstPercentage(extendedJobDocument.job_tax_rate);
	const mwst = roundToCents(subTotal * (mwstPercentage / 100));

	// Total (subtract akonto if present)
	const totalBeforeAkonto = applyRounding(subTotal + mwst, roundingType);
	const roundingDifference = roundToCents(
		totalBeforeAkonto - (subTotal + mwst)
	);
	const total =
		akontoAmount !== undefined && akontoAmount > 0
			? applyRounding(totalBeforeAkonto - akontoAmount, roundingType)
			: totalBeforeAkonto;

	const drTaxAmount = calculateSubjectToDrTaxAmounts(jobItems);

	const subjectToDrTaxAmountExclVat = roundToCents(drTaxAmount);
	const exemptFromTaxAmountExclVat = roundToCents(
		subTotal - subjectToDrTaxAmountExclVat
	);
	const subjectToDrTaxAmountInclVat = roundToCents(
		drTaxAmount * (1 + mwstPercentage / 100)
	);
	const exemptFromTaxAmountInclVat = roundToCents(
		(subTotal - subjectToDrTaxAmountExclVat) * (1 + mwstPercentage / 100)
	);

	return {
		taxpunkte: getCalculationResult(taxpunkte),
		amountPostage: getCalculationResult(amountPostage),
		amountExternalWork: getCalculationResult(amountExternalWork),
		amountDiscounts: getCalculationResult(amountDiscounts),
		amountWorkAndFixedRate: getCalculationResult(amountWorkAndFixedRate),
		deductions: getCalculationResult(deductions),
		deductionsWorkAndFixedRate: getCalculationResult(
			decuctionsWorkAndFixedRate
		),
		deductionsMaterials: getCalculationResult(decuctionsMaterials),
		amountMaterials: getCalculationResult(amountMaterials),
		subTotal: getCalculationResult(subTotal),
		mwst: getCalculationResult(mwst),
		total: getCalculationResult(total),
		exemptFromTaxAmountExclVat: getCalculationResult(
			exemptFromTaxAmountExclVat
		),
		exemptFromTaxAmountInclVat: getCalculationResult(
			exemptFromTaxAmountInclVat
		),
		subjectToDrTaxAmountExclVat: getCalculationResult(
			subjectToDrTaxAmountExclVat
		),
		subjectToDrTaxAmountInclVat: getCalculationResult(
			subjectToDrTaxAmountInclVat
		),
		mwstPercentage: mwstPercentage.toString(),
		akontoReallocation:
			akontoAmount !== undefined && akontoAmount > 0
				? getCalculationResult(akontoAmount)
				: undefined,
		roundingDifference: getCalculationResult(roundingDifference),
	};
}

export function calculateSammelrechnungRowsAndTotals(
	extendedJobDocuments: ExtendedJobDocument[],
	organization: OrganizationEntityType,
	recipient: ClientEntityType | GuarantorEntityType,
	clientsLookup: Record<string, ClientEntityType>,
	mwstPercentage: number,
	akontoAmount?: number
): CalculateSammelrechnungResult {
	const rowsForPdfTable: SammelrechnungRow[] = extendedJobDocuments.map(
		(extendedJobDocument) => {
			const client =
				clientsLookup[extendedJobDocument.job_client_id || ""];
			return {
				jobCode: extendedJobDocument.job_code || "",
				clientName: getClientDisplayName(client),
				patientName: {
					title: extendedJobDocument.patient_title || "",
					firstName: extendedJobDocument.patient_first_name || "",
					lastName: extendedJobDocument.patient_last_name || "",
					birth_date: extendedJobDocument.patient_birth_date || "",
					code: extendedJobDocument.patient_code || "",
				},
				practitionerName: {
					title: extendedJobDocument.practitioner_title || "",
					firstName:
						extendedJobDocument.practitioner_first_name || "",
					lastName: extendedJobDocument.practitioner_last_name || "",
					id: extendedJobDocument.practitioner_id || null,
				},
				jobDocumentDate: extendedJobDocument.job_document_date || "",
				jobTitle: extendedJobDocument.job_title || "",
				jobTotals:
					extendedJobDocument.job_document_type ===
					JobDocumentTypeEnum.CreditNote
						? calculateJobTotals({
								extendedJobDocument: {
									...extendedJobDocument,
									job_items:
										extendedJobDocument.job_items.map(
											(item) => {
												return {
													...item,
													price: -item.price,
												};
											}
										),
								},
								organization,
								client,
							})
						: calculateJobTotals({
								extendedJobDocument,
								organization,
								client,
							}),
				ignoreMonthlyDiscount:
					extendedJobDocument.is_ignore_monthly_discount || false,
			};
		}
	);

	const monthlyDiscount = recipient?.monthly_discount || 0;
	const isMonthlyDiscountOnlyWork = (recipient as ClientEntityType)
		?.is_monthly_discount_only_work;

	const roundingType =
		(extendedJobDocuments[0]?.job_round_to as RoundToType) ??
		RoundToType.TO_SECOND_DECIMAL;

	const rowMonthlyDiscounts = rowsForPdfTable.map(
		({ jobTotals, ignoreMonthlyDiscount }) => {
			if (monthlyDiscount <= 0 || ignoreMonthlyDiscount) {
				return { onWorkAndFixedRate: 0, onSubtotal: 0 };
			}
			if (isMonthlyDiscountOnlyWork) {
				const amountWorkAfterJobDocumentDiscount = roundToCents(
					jobTotals.amountWorkAndFixedRate.value -
						jobTotals.deductionsWorkAndFixedRate.value
				);
				return {
					onWorkAndFixedRate: roundToCents(
						amountWorkAfterJobDocumentDiscount *
							(monthlyDiscount / 100)
					),
					onSubtotal: 0,
				};
			}
			return {
				onWorkAndFixedRate: 0,
				onSubtotal: roundToCents(
					jobTotals.subTotal.value * (monthlyDiscount / 100)
				),
			};
		}
	);

	const netByRate = new Map<number, number>();
	rowsForPdfTable.forEach(({ jobTotals }, index) => {
		const rate = getMwstPercentage(
			extendedJobDocuments[index].job_tax_rate
		);
		const { onWorkAndFixedRate, onSubtotal } = rowMonthlyDiscounts[index];
		netByRate.set(
			rate,
			(netByRate.get(rate) ?? 0) +
				jobTotals.subTotal.value -
				onWorkAndFixedRate -
				onSubtotal
		);
	});

	// VAT is due on the collective net per rate after the monthly discount, not on the documents' own VAT.
	const vatLines: VatLine[] = Array.from(netByRate, ([rate, net]) => {
		const roundedNet = roundToCents(net);
		return {
			rate,
			net: roundedNet,
			vat: roundToCents(roundedNet * (rate / 100)),
		};
	}).sort((a, b) => b.rate - a.rate);

	const bookableDocumentsTotalsResult = rowsForPdfTable.reduce(
		(acc, curr, index) => {
			const jobTotals = curr.jobTotals;

			return {
				amountWorkAndFixedRate:
					acc.amountWorkAndFixedRate +
					roundToCents(jobTotals.amountWorkAndFixedRate.value),
				amountMaterials:
					acc.amountMaterials +
					roundToCents(jobTotals.amountMaterials.value),
				amountPostage:
					acc.amountPostage +
					roundToCents(jobTotals.amountPostage.value),
				amountExternalWork:
					acc.amountExternalWork +
					roundToCents(jobTotals.amountExternalWork.value),
				amountDiscounts:
					acc.amountDiscounts +
					roundToCents(jobTotals.amountDiscounts.value),
				deductionsWorkAndFixedRate:
					acc.deductionsWorkAndFixedRate +
					roundToCents(jobTotals.deductionsWorkAndFixedRate.value),
				deductionsMaterials:
					acc.deductionsMaterials +
					roundToCents(jobTotals.deductionsMaterials.value),
				deductions:
					acc.deductions + roundToCents(jobTotals.deductions.value),
				taxpunkte: acc.taxpunkte + jobTotals.taxpunkte.value,
				subectToTaxAmount:
					acc.subectToTaxAmount +
					jobTotals.subjectToDrTaxAmountExclVat.value,
				exemptFromTaxAmount:
					acc.exemptFromTaxAmount +
					jobTotals.exemptFromTaxAmountExclVat.value,
				subjectToTaxAmountInclVat:
					acc.subjectToTaxAmountInclVat +
					jobTotals.subjectToDrTaxAmountInclVat.value,
				exemptFromTaxAmountInclVat:
					acc.exemptFromTaxAmountInclVat +
					jobTotals.exemptFromTaxAmountInclVat.value,
				monthlyDiscountOnWorkAndFixedRate:
					acc.monthlyDiscountOnWorkAndFixedRate +
					rowMonthlyDiscounts[index].onWorkAndFixedRate,
				monthlyDiscountOnSubtotal:
					acc.monthlyDiscountOnSubtotal +
					rowMonthlyDiscounts[index].onSubtotal,
			};
		},
		{
			amountWorkAndFixedRate: 0,
			amountMaterials: 0,
			amountPostage: 0,
			amountExternalWork: 0,
			amountDiscounts: 0,
			deductions: 0,
			deductionsWorkAndFixedRate: 0,
			deductionsMaterials: 0,
			taxpunkte: 0,
			subectToTaxAmount: 0,
			exemptFromTaxAmount: 0,
			subjectToTaxAmountInclVat: 0,
			exemptFromTaxAmountInclVat: 0,
			monthlyDiscountOnWorkAndFixedRate: 0,
			monthlyDiscountOnSubtotal: 0,
		}
	);

	const { earliest, latest } = findEarliestAndLatest(
		extendedJobDocuments,
		"job_document_date"
	);

	const monthlyDiscountOnSubtotal = roundToCents(
		bookableDocumentsTotalsResult.monthlyDiscountOnSubtotal
	);
	const monthlyDiscountOnWorkAndFixedRate = roundToCents(
		bookableDocumentsTotalsResult.monthlyDiscountOnWorkAndFixedRate
	);

	const subtotal = roundToCents(
		vatLines.reduce((sum, vatLine) => sum + vatLine.net, 0)
	);
	const mwst = roundToCents(
		vatLines.reduce((sum, vatLine) => sum + vatLine.vat, 0)
	);

	const totalBeforeRounding =
		akontoAmount !== undefined && akontoAmount > 0
			? subtotal + mwst - akontoAmount
			: subtotal + mwst;

	const total = applyRounding(totalBeforeRounding, roundingType);

	const roundingDifference = roundToCents(
		total - (subtotal + mwst - (akontoAmount ?? 0))
	);

	const subjectToDrTaxAmountExclVat =
		bookableDocumentsTotalsResult.subectToTaxAmount;
	const exemptFromTaxAmountExclVat =
		bookableDocumentsTotalsResult.exemptFromTaxAmount;
	const exemptFromTaxAmountInclVat =
		bookableDocumentsTotalsResult.exemptFromTaxAmountInclVat;
	const subjectToDrTaxAmountInclVat =
		bookableDocumentsTotalsResult.subjectToTaxAmountInclVat;

	// Extract job_mwst_number from the first job document (all jobs in a Sammelrechnung should have the same value)
	const jobMwstNumber =
		extendedJobDocuments.length > 0
			? extendedJobDocuments[0].job_mwst_number ?? undefined
			: undefined;

	return {
		rows: rowsForPdfTable,
		amountWorkAndFixedRate: getCalculationResult(
			bookableDocumentsTotalsResult.amountWorkAndFixedRate
		),
		amountMaterials: getCalculationResult(
			bookableDocumentsTotalsResult.amountMaterials
		),
		amountPostage: getCalculationResult(
			bookableDocumentsTotalsResult.amountPostage
		),
		amountExternalWork: getCalculationResult(
			bookableDocumentsTotalsResult.amountExternalWork
		),
		amountDiscounts: getCalculationResult(
			bookableDocumentsTotalsResult.amountDiscounts
		),
		deductionsWorkAndFixedRate: getCalculationResult(
			bookableDocumentsTotalsResult.deductionsWorkAndFixedRate
		),
		deductionsMaterials: getCalculationResult(
			bookableDocumentsTotalsResult.deductionsMaterials
		),
		deductions: getCalculationResult(
			bookableDocumentsTotalsResult.deductions
		),
		taxpunkte: getCalculationResult(
			bookableDocumentsTotalsResult.taxpunkte
		),
		exemptFromTaxAmountExclVat: getCalculationResult(
			exemptFromTaxAmountExclVat
		),
		exemptFromTaxAmountInclVat: getCalculationResult(
			exemptFromTaxAmountInclVat
		),
		subjectToDrTaxAmountExclVat: getCalculationResult(
			subjectToDrTaxAmountExclVat
		),
		subjectToDrTaxAmountInclVat: getCalculationResult(
			subjectToDrTaxAmountInclVat
		),
		monthlyDiscountOnWorkAndFixedRate: getCalculationResult(
			monthlyDiscountOnWorkAndFixedRate
		),
		monthlyDiscountOnSubtotal: getCalculationResult(
			monthlyDiscountOnSubtotal
		),
		subTotal: getCalculationResult(subtotal),
		mwst: getCalculationResult(mwst),
		total: getCalculationResult(total),
		startDate: earliest,
		endDate: latest,
		mwstPercentage: mwstPercentage.toString(),
		vatLines,
		jobMwstNumber,
		akontoReallocation:
			akontoAmount !== undefined && akontoAmount > 0
				? getCalculationResult(akontoAmount)
				: undefined,
		roundingDifference: getCalculationResult(roundingDifference),
	};
}

interface JobItemEntityTypeForPdfTable
	extends Omit<JobItemEntityType, "price" | "tp_value"> {
	tp_value: string;
	price: string;
	total: string;
}

/**
 * Take job items and
 * - calculates the total amount for each row and returns it as a string rounded to 2 decimal places
 * - adds two decimal places to the price field (i.e. appends .00/.x0)
 * - adds to decimal places to tp_value (TP-Wert)
 * @param {JobItemEntityType[]} jobItems - jobItems to calculate total for
 * @returns {JobItemEntityTypeForPdfTable[]} - jobItems with total field added
 */
export function getJobItemsForPdfTable(
	jobItems: JobItemEntityType[]
): JobItemEntityTypeForPdfTable[] {
	return jobItems
		.map((jobItem) => {
			let price = "0.00";
			try {
				price = jobItem.price.toFixed(2);
			} catch (error) {
				Logger.error(error, {}, "Error converting price to string");
			}
			return {
				...jobItem,
				tp_value: jobItem?.tp_value?.toFixed(2) || "0.00",
				price,
				total: calculateJobItemAmountForJobItem(jobItem).toFixed(2),
			};
		})
		.sort((a, b) => {
			// Manual ordering wins when present. `position` is null on legacy items,
			// which fall back to the old default (type, then code). Positioned items
			// always come before unpositioned ones so a partially-ordered document
			// stays stable.
			const pa = a.position ?? null;
			const pb = b.position ?? null;
			if (pa !== null && pb !== null) {
				if (pa !== pb) return pa - pb;
			} else if (pa !== null) {
				return -1;
			} else if (pb !== null) {
				return 1;
			}
			// First sort by type (numeric enum)
			const typeDiff = (a.type ?? 0) - (b.type ?? 0);
			if (typeDiff !== 0) return typeDiff;
			// Then sort by code
			return (a.code ?? "").localeCompare(b.code ?? "");
		});
}

/**
 * Cacluates the prepayment for a job, returns 0 if the solvency limit is above the total amount
 * @param solvencyLimit - taken from the organization settings
 * @param prepaymentPercentage - taken from the organization settings, the percentage of the prepayment (e.g. 10)
 * @param totalAmount - the total amount of the job (after discounts, taxes,...) 

 * @returns the prepayment amount
*/
export function calculatePrePayment(
	solvencyLimit: number,
	prepaymentPercentage: number,
	totalAmount: number
): CalculationResult {
	if (solvencyLimit > totalAmount) {
		return getCalculationResult(0);
	}
	const prepayment = solvencyLimit * (prepaymentPercentage / 100);
	return getCalculationResult(prepayment);
}
